import React, { useEffect, useMemo, useRef, useState } from 'react';
import { tipsService } from '../../services/tipsService';

type Props = {
  toCreatorId: string;
  toCreatorName?: string;
  defaultAmount?: number;
  currency?: string;
  onClose: () => void;
  onSuccess?: (tipId: string) => void;
};

const TipModal: React.FC<Props> = ({ toCreatorId, toCreatorName, defaultAmount = 5, currency = 'USD', onClose, onSuccess }) => {
  const [amount, setAmount] = useState<number>(defaultAmount);
  const [message, setMessage] = useState<string>('');
  const [isAnonymous, setIsAnonymous] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [tipId, setTipId] = useState<string | null>(null);
  const [step, setStep] = useState<'details' | 'payment' | 'done'>('details');

  const stripeRef = useRef<any>(null);
  const elementsRef = useRef<any>(null);
  const paymentElementRef = useRef<HTMLDivElement>(null);

  const formattedAmount = useMemo(() => new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount || 0), [amount, currency]);

  useEffect(() => {
    const initElements = async () => {
      if (!clientSecret || !paymentElementRef.current) return;
      const pk = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
      if (!pk) {
        setError('Stripe is not configured. Missing VITE_STRIPE_PUBLISHABLE_KEY');
        return;
      }
      const { loadStripe } = await import('@stripe/stripe-js');
      const stripe = await loadStripe(pk);
      if (!stripe) {
        setError('Failed to load Stripe');
        return;
      }
      stripeRef.current = stripe;
      const elements = stripe.elements({ clientSecret, appearance: { theme: 'stripe' } });
      elementsRef.current = elements;
      const paymentElement = elements.create('payment');
      paymentElement.mount(paymentElementRef.current);
    };
    initElements();
  }, [clientSecret]);

  const startPayment = async () => {
    setLoading(true);
    setError(null);
    try {
      const { clientSecret, tipId } = await tipsService.createTip({ toCreatorId, amount, currency, message, isAnonymous });
      setClientSecret(clientSecret);
      setTipId(tipId);
      setStep('payment');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to start payment');
    } finally {
      setLoading(false);
    }
  };

  const confirmPayment = async () => {
    if (!stripeRef.current || !elementsRef.current || !clientSecret) return;
    setLoading(true);
    setError(null);
    try {
      const stripe = stripeRef.current;
      const elements = elementsRef.current;
      const result = await stripe.confirmPayment({ elements, clientSecret, redirect: 'if_required' });
      if (result.error) {
        setError(result.error.message || 'Payment failed');
        return;
      }
      // Optionally verify tip status
      if (tipId) {
        try { await tipsService.getTip(tipId); } catch {}
      }
      setStep('done');
      onSuccess?.(tipId || '');
    } catch (e: any) {
      setError(e?.message || 'Payment confirmation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Send a tip {toCreatorName ? `to ${toCreatorName}` : ''}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>

        {step === 'details' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Amount</label>
              <div className="mt-1 flex items-center gap-2">
                <input type="number" min={1} step={1} value={amount} onChange={e => setAmount(Number(e.target.value))} className="w-32 border rounded-md px-3 py-2" />
                <span className="text-sm text-gray-600">{formattedAmount}</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Message (optional)</label>
              <textarea value={message} onChange={e => setMessage(e.target.value)} rows={3} className="w-full border rounded-md px-3 py-2" placeholder="Say thanks or add a note" />
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={isAnonymous} onChange={e => setIsAnonymous(e.target.checked)} />
              Tip anonymously
            </label>
            {error && <div className="text-sm text-red-600">{error}</div>}
            <button onClick={startPayment} disabled={loading || amount <= 0} className="w-full bg-indigo-600 text-white rounded-md py-2 hover:bg-indigo-700 disabled:opacity-50">
              {loading ? 'Starting…' : `Continue to pay ${formattedAmount}`}
            </button>
          </div>
        )}

        {step === 'payment' && (
          <div className="space-y-4">
            <div ref={paymentElementRef} />
            {error && <div className="text-sm text-red-600">{error}</div>}
            <button onClick={confirmPayment} disabled={loading} className="w-full bg-green-600 text-white rounded-md py-2 hover:bg-green-700 disabled:opacity-50">
              {loading ? 'Processing…' : `Pay ${formattedAmount}`}
            </button>
            <button onClick={() => setStep('details')} className="w-full text-gray-600 border rounded-md py-2 hover:bg-gray-50">
              Back
            </button>
          </div>
        )}

        {step === 'done' && (
          <div className="space-y-4 text-center">
            <div className="text-green-600 text-3xl">✓</div>
            <div className="text-lg font-medium">Thanks for your support!</div>
            <div className="text-sm text-gray-600">Your tip has been processed.</div>
            <button onClick={onClose} className="w-full bg-indigo-600 text-white rounded-md py-2 hover:bg-indigo-700">
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TipModal;
